import { t, useLocale, errorMessage } from '../lib/i18n.js';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DEFAULT_WRONG_PENALTY,
  LETTERS,
  RoomCodeSchema,
  pointsForCorrect,
  type ClientMessage,
} from '@listen-draw/shared';
import { keys, storage } from '../lib/api.js';
import { GameSocket } from '../lib/socket.js';
import { useStore } from '../lib/store.js';
import { Player as TtsPlayer } from '../lib/tts.js';
import { useCountdownSeconds, useServerNow } from '../lib/time.js';
import { Canvas, drawingPct } from '../components/Canvas.js';
import { Choices } from '../components/Choices.js';
import { Ranking } from '../components/Ranking.js';
import { ConnStatus } from '../components/ConnStatus.js';
import { ShareResult } from '../components/ShareResult.js';
import { Explanation } from '../components/Explanation.js';
import { playRecording, SOUND_CHECK, unlockAudio } from '../lib/sound.js';
import { track } from '../lib/analytics.js';
import { Podium } from '../components/Podium.js';

type Stamp = { text: string; kind: 'pick' | 'ok' | 'muted' } | null;

export function Play() {
  useLocale();
  const params = useParams();
  const nav = useNavigate();
  const codeParsed = RoomCodeSchema.safeParse(params.code ?? '');
  const code = codeParsed.success ? codeParsed.data : '';
  // 名前は任意。空ならサーバーがゲスト名（動物 + 2 桁）を割り当てる = 匿名参加
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const sockRef = useRef<GameSocket | null>(null);

  const state = useStore((s) => s.state);
  const playerId = useStore((s) => s.playerId);
  const token = useStore((s) => s.token);
  const audio = useStore((s) => s.audio);
  const events = useStore((s) => s.events);
  const fatal = useStore((s) => s.fatal);
  const toast = useStore((s) => s.toast);
  const myAnswerRaw = useStore((s) => s.myAnswer);
  const [progress, setProgress] = useState<number | null>(null);
  const [hintUpTo, setHintUpTo] = useState<number>(-1);
  const [stamp, setStamp] = useState<Stamp>(null);
  const [soundReady, setSoundReady] = useState(false);
  const [soundError, setSoundError] = useState('');
  const testSound = useRef<AbortController | null>(null);
  useEffect(() => () => testSound.current?.abort(), []);
  const enableSound = async () => {
    testSound.current?.abort();
    const controller = new AbortController();
    testSound.current = controller;
    try {
      await unlockAudio();
      await playRecording(SOUND_CHECK, 1, controller.signal);
      setSoundReady(true);
      setSoundError('');
    } catch {
      if (!controller.signal.aborted)
        setSoundError(
          t(
            'Could not play sound. Check your connection.',
            '音声を再生できません。接続を確認してください。',
          ),
        );
    }
  };
  const [showCanvas, setShowCanvas] = useState(true);

  const send = useCallback(
    (m: ClientMessage) => {
      if (!sockRef.current?.send(m))
        toast(t('Not connected. Reconnecting…', 'サーバーに接続していません'), 'bad');
    },
    [toast],
  );

  // 接続。ページを開いたら即入室する（保存済みの名前があれば送り、無ければサーバーがゲスト名を付ける）
  useEffect(() => {
    if (!code) return;
    const hello: Extract<ClientMessage, { type: 'hello' }> = {
      type: 'hello',
      role: 'player',
      roomCode: code,
    };
    const t = storage.get(keys.player(code));
    if (t) hello.playerToken = t;
    const savedName = storage.get(keys.name)?.trim();
    if (savedName) hello.name = savedName;
    useStore.setState({ token: t });
    const s = new GameSocket(hello);
    s.connect();
    sockRef.current = s;
    return () => {
      s.close();
      sockRef.current = null;
      useStore.getState().reset();
    };
  }, [code]);

  useEffect(() => {
    if (token && code) storage.set(keys.player(code), token);
  }, [token, code]);

  // オンラインモード: 自分の端末で読み上げ
  useEffect(() => {
    if (!audio || !state || state.settings.audioTarget === 'host') return;
    testSound.current?.abort();
    const player = new TtsPlayer({
      items: audio.items,
      rate: audio.rate,
      gapMs: audio.gapMs,
      speak: true,
      onItemStart: (i) => {
        setProgress(i);
        setHintUpTo((h) => Math.max(h, i));
      },
      onDone: () => setProgress(null),
      onError: () => {
        setSoundReady(false);
        setSoundError(
          t(
            'Sound stopped. Enable sound, then ask the host to replay.',
            '音声が停止しました。音声を有効にして、ホストに再生を依頼してください。',
          ),
        );
        track('audio_error', { mode: 'multiplayer' });
      },
    });
    player.start();
    return () => player.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio?.id]);

  // Host の読み上げ進行に合わせて強調＆ヒント公開
  const lastEvent = events[0];
  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === 'audio_progress' && lastEvent.letterIndex !== undefined) {
      setProgress(lastEvent.letterIndex);
      setHintUpTo((h) => Math.max(h, lastEvent.letterIndex!));
    }
    if (lastEvent.type === 'round_aborted')
      toast(t('Restarting the round', 'ラウンドをやり直します'));
  }, [lastEvent, toast]);

  const phase = state?.phase ?? 'lobby';
  const round = state?.round ?? null;
  const roundIndex = round?.index;
  // 自分の回答（前のラウンドのものは無視する）
  const myAnswer =
    myAnswerRaw && roundIndex !== undefined && myAnswerRaw.roundIndex === roundIndex
      ? myAnswerRaw
      : null;
  const myOutcome = round?.reveal?.outcomes.find((o) => o.playerId === playerId) ?? null;

  useEffect(() => {
    // ラウンドが変わったらリセット
    setProgress(null);
    setHintUpTo(-1);
    setStamp(null);
  }, [roundIndex]);

  // 押した直後: 選んだ文字をスタンプ（正誤はまだ分からない）
  useEffect(() => {
    if (!myAnswer) return;
    setStamp({ text: `${LETTERS[myAnswer.letterIndex]}!`, kind: 'pick' });
    navigator.vibrate?.(40);
  }, [myAnswer]);

  // 公開: 正誤スタンプ
  useEffect(() => {
    if (myOutcome && roundIndex !== undefined)
      track(
        'round_complete',
        { mode: 'multiplayer', correct: myOutcome.correct, pct: myOutcome.pct },
        `${code}:${state?.createdAt}:${roundIndex}`,
      );
  }, [myOutcome, roundIndex, code, state?.createdAt]);

  const revealedCorrect = round?.reveal ? (myOutcome?.correct ?? null) : undefined;
  useEffect(() => {
    if (revealedCorrect === undefined) return;
    if (revealedCorrect === null) {
      setStamp(null);
      return;
    }
    setStamp(revealedCorrect ? { text: 'CORRECT', kind: 'ok' } : { text: 'MISS', kind: 'muted' });
    navigator.vibrate?.(revealedCorrect ? [30, 40, 30] : 120);
  }, [revealedCorrect]);

  const me = state?.players.find((p) => p.id === playerId) ?? null;
  const active = phase === 'drawing' || phase === 'final';
  const now = useServerNow(active && !!round?.drawing && round.drawing.pausedAt === null, 10);
  const pct = drawingPct(round?.drawing, now, phase === 'reveal' || phase === 'results');
  const canPick = !!me && active && !myAnswer && me.status === 'idle';
  const finalSecs = useCountdownSeconds(round?.final?.until, phase === 'final');
  const pointsNow = pointsForCorrect(pct, {
    finalMode: phase === 'final',
    halved: round?.audio.halved ?? false,
  });
  const penalty = state?.settings.wrongPenalty ?? DEFAULT_WRONG_PENALTY;
  const myRank = me ? (state?.players.findIndex((p) => p.id === me.id) ?? -1) + 1 : 0;
  const winner = round?.reveal?.winnerPlayerId
    ? (state?.players.find((p) => p.id === round.reveal!.winnerPlayerId) ?? null)
    : null;

  const submitName = (e: FormEvent) => {
    e.preventDefault();
    const n = draftName.trim().slice(0, 20);
    setEditingName(false);
    if (!n || n === me?.name) return;
    storage.set(keys.name, n);
    send({ type: 'player:rename', name: n });
  };

  if (!code) {
    return (
      <main className="page page--narrow">
        <h1>{t('Enter a room code', 'ルームコードが必要です')}</h1>
        <Link className="btn btn--primary" to="/">
          {t('Home ', 'トップへ ')}
        </Link>
      </main>
    );
  }
  if (fatal) {
    return (
      <main className="page page--narrow">
        <div className="phone">
          <h2>{t('Could not join', '入室できません')}</h2>
          <p className="error">{errorMessage(fatal.code)}</p>
          <button
            className="btn btn--primary"
            onClick={() => {
              storage.remove(keys.player(code));
              useStore.getState().setFatal(null);
              nav('/');
            }}
          >
            {t('Back to home ', 'トップへ戻る ')}
          </button>
        </div>
      </main>
    );
  }
  return (
    <main className="page page--narrow" style={{ paddingInline: 12 }}>
      <div className="phone">
        <div className="phone__head">
          <span>{phaseLabelForPlayer(phase, !!myAnswer)}</span>
          <span>
            {me?.name ?? '…'} · <b className="num">{me?.score ?? 0}</b> {t(' points ', '点 ')}
            {myRank ? t(` · #${myRank}`, ` · ${myRank} 位`) : ''}
          </span>
        </div>
        <ConnStatus />
        {state && state.settings.audioTarget !== 'host' && (phase === 'lobby' || !soundReady) && (
          <button className="btn" onClick={() => void enableSound()}>
            {soundReady
              ? t('✓ Sound ready · test again', '✓ 音声OK · 再テスト')
              : t('♫ Enable sound on this device', '♫ この端末の音声を有効にする')}
          </button>
        )}
        {soundError && (
          <p className="error" role="alert">
            {soundError}
          </p>
        )}

        {phase === 'lobby' && (
          <div className="stack" style={{ flex: 1, justifyContent: 'center', textAlign: 'center' }}>
            <div className="code-big num" style={{ fontSize: 36 }}>
              {code}
            </div>
            {editingName ? (
              <form className="me-form" onSubmit={submitName}>
                <div className="field">
                  <label htmlFor="me-name">{t('Your name', 'なまえ')}</label>
                  <input
                    id="me-name"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    maxLength={20}
                    autoFocus
                  />
                </div>
                <button className="btn btn--primary" type="submit">
                  {t('Save', '決定 ')}
                </button>
              </form>
            ) : (
              <div className="me-line">
                <span className="muted small">{t('You:', 'あなた:')}</span>
                <span className="me-name">{me?.name ?? '…'}</span>
                <button
                  className="btn btn--ghost small"
                  type="button"
                  onClick={() => {
                    setDraftName(me?.name ?? '');
                    setEditingName(true);
                  }}
                >
                  {t('Change name', '名前を変える ')}
                </button>
              </div>
            )}
            <p className="muted">
              {t(
                'Waiting for the host to start the game.',
                'Host がゲームを始めるまでお待ちください。',
              )}
            </p>
            <p className="muted small">
              {t('Players ', '参加者 ')}
              {state?.players.length ?? 0} {t(' players', '人')}
            </p>
            {state && <Ranking players={state.players} meId={playerId} limit={6} />}
          </div>
        )}

        {phase === 'results' && state && (
          <div className="stack">
            <Podium players={state.players} />
            <Ranking players={state.players} meId={playerId} />
            <Link className="btn" to="/">
              {t('Home ', 'トップへ ')}
            </Link>
          </div>
        )}

        {phase !== 'lobby' && phase !== 'results' && (
          <>
            <Choices
              round={round}
              playing={phase === 'listening' ? progress : null}
              hintUpTo={
                state?.settings.hint && !round?.reveal
                  ? round?.audio.done
                    ? undefined
                    : hintUpTo
                  : undefined
              }
              myLetter={myOutcome?.letterIndex ?? null}
              onPick={(i) => {
                if (canPick) send({ type: 'answer', letterIndex: i });
              }}
              pickable={canPick}
              picked={myAnswer?.letterIndex ?? null}
              compact
            />

            {(active ||
              phase === 'countdown_draw' ||
              phase === 'preparing' ||
              phase === 'reveal') &&
              showCanvas && <Canvas round={round} phase={phase} mini stamp={stamp} />}
            {phase === 'countdown_audio' && round?.countdown && (
              <Canvas round={round} phase={phase} mini />
            )}

            {(phase === 'countdown_audio' ||
              phase === 'listening' ||
              phase === 'preparing' ||
              phase === 'countdown_draw') && (
              <p className="center muted small" style={{ margin: 0 }}>
                {t(
                  'Choose A–D once the drawing starts. You get one answer per round.',
                  '描画が始まったら A〜D を押して回答。押せるのは 1 回だけ、描画は止まりません。 ',
                )}
              </p>
            )}

            {active && (
              <>
                <div className="progress">
                  <span>
                    {t('Sketch ', '線画の完成率 ')}
                    <b className="num">{pct}%</b>
                  </span>
                  <span>
                    {myAnswer
                      ? t(
                          `Answered: ${LETTERS[myAnswer.letterIndex]} (at ${myAnswer.pct}%)`,
                          `回答済み: ${LETTERS[myAnswer.letterIndex]}（完成率 ${myAnswer.pct}%）`,
                        )
                      : phase === 'final'
                        ? t(
                            `${finalSecs}s left · Correct now: +${pointsNow}`,
                            `残り ${finalSecs} 秒 · いま押して正解なら +${pointsNow}`,
                          )
                        : t(
                            `Correct now: +${pointsNow} · Wrong: −${penalty}`,
                            `いま押して正解なら +${pointsNow} · 誤答 −${penalty}`,
                          )}
                  </span>
                </div>
                <div className="bar">
                  <i style={{ width: `${pct}%` }} />
                </div>
                {myAnswer && (
                  <p className="center muted small" style={{ margin: 0 }}>
                    {t(
                      'Your answer is locked. Results appear when the drawing finishes.',
                      '描画が終わると答え合わせ。正解なら押した早さで順位が決まります。 ',
                    )}
                  </p>
                )}
              </>
            )}

            {round?.reveal && (
              <div className="card" style={{ padding: 12 }}>
                <b>
                  {t('Answer: ', '正解は ')}
                  {LETTERS[round.reveal.correctLetterIndex]}
                </b>
                {myOutcome ? (
                  <div className="num">
                    {myOutcome.correct ? t('Correct! ', '正解！ ') : t('Incorrect ', '不正解 ')}
                    <b style={{ color: myOutcome.points >= 0 ? 'var(--ok)' : 'var(--buzz)' }}>
                      {myOutcome.points >= 0 ? '+' : ''}
                      {myOutcome.points} {t(' points ', '点 ')}
                    </b>
                    <span className="muted small">
                      {t(' (at ', '（完成率 ')}
                      {myOutcome.pct}
                      {t('%: chose ', '% で ')}
                      {LETTERS[myOutcome.letterIndex]} {t(' ', 'を選択 ')}
                      {myOutcome.rank
                        ? t(
                            ` · #${myOutcome.rank} among correct answers`,
                            ` · 正解者の中で ${myOutcome.rank} 番目`,
                          )
                        : ''}
                      ）
                    </span>
                  </div>
                ) : (
                  <div className="muted small">
                    {t('You did not answer this round.', 'このラウンドは回答しませんでした。')}
                  </div>
                )}
                {myOutcome && (
                  <ShareResult
                    correct={myOutcome.correct}
                    pct={myOutcome.pct}
                    points={myOutcome.points}
                  />
                )}
                {winner && (
                  <div className="small">
                    {t('Fastest correct: ', '最速正解: ')}
                    <b>{winner.name}</b>
                    {winner.id === playerId ? t(' (you!)', '（あなた！）') : ''}
                  </div>
                )}
                <div className="muted small">
                  {t('Listen for: ', '決め手: ')}
                  {round.reveal.keyWords.join(' / ')}
                </div>
              </div>
            )}

            {round?.reveal && (
              <Explanation text={round.reveal.texts[round.reveal.correctLetterIndex]!} />
            )}
            <label
              className="small muted"
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 'auto',
              }}
            >
              <input
                type="checkbox"
                checked={showCanvas}
                onChange={(e) => setShowCanvas(e.target.checked)}
              />
              {t('Show the sketch on my screen ', '手元にも線画を表示 ')}
            </label>
          </>
        )}
      </div>
    </main>
  );
}

function phaseLabelForPlayer(phase: string, answered: boolean): string {
  switch (phase) {
    case 'lobby':
      return t('Waiting', '待機中');
    case 'countdown_audio':
      return t('Audio starts soon', 'まもなく音声');
    case 'listening':
      return t('Listen to the four options', '4つの英文を聞こう');
    case 'preparing':
      return t('Preparing sketch', '線画を準備中');
    case 'countdown_draw':
      return t('Drawing starts soon', 'まもなく描画');
    case 'drawing':
      return answered
        ? t('Drawing — answer locked', '描画中 — 回答済み')
        : t('Drawing — choose A–D!', '描画中 — わかったら A〜D を押す！');
    case 'final':
      return answered
        ? t('Last chance — answer locked', '最終回答タイム — 回答済み')
        : t('Last chance', '最終回答タイム');
    case 'reveal':
      return t('Reveal', '答え合わせ');
    case 'results':
      return t('Results', '結果発表');
    default:
      return '';
  }
}
